import {t, Trans} from '@lingui/macro'
import {useEffect} from 'react'
import styled from 'styled-components'

import {
    Box,
    Button,
    Inline,
    Link,
    Loading,
    ModalContent,
    ModalFooter,
    ModalTitle,
    Tag,
    Text,
    tokens,
    Tooltip,
} from '@pleo-io/telescope'
import {CheckSmall, HighFive} from '@pleo-io/telescope-icons'

import {PlanTypeName} from '@product-web/api-types/billing'
import config from '@product-web/config'
import {reportError} from '@product-web/error/report'
import {useFlags} from '@product-web/flags'
import {breakpoints} from '@product-web/styles/theme'
import {useToaster} from '@product-web/toaster'
import {useMediaQuery} from '@product-web/web-platform/use-media-query'

import type {AllowedPlanType, PlanPrices} from './plan-update.helper'
import {
    DashedText,
    getPlanUpdateModalConf,
    getPlanUpdatePagesConf,
    SmallText,
    starterAddonsPricesPerCurrencyMap,
    trackChoosePlanModalActioned,
} from './plan-update.helper'

import {bff} from '../bff-hooks'
import {toStringNoDecimal} from '../manage-plan/pricing-helpers'
import type {PageOrigin, PageSection} from '../types'

interface PlanUpdateStepOneProps {
    pageOrigin: PageOrigin
    pageSection?: PageSection
    onGetStarted: ({
        upgradeToPlan,
        existingPlan,
    }: {
        upgradeToPlan: AllowedPlanType
        existingPlan: AllowedPlanType
    }) => void
}

const advancedFeatures: Partial<PageOrigin>[] = [
    'budgets',
    'vendor-cards',
    'add-people-integrations-promo',
    'company-integrations-people-management-promo',
    'people-management-app-details',
    'settings-accounting',
]

export const PlanUpdateStepOne = ({
    pageOrigin,
    pageSection,
    onGetStarted,
}: PlanUpdateStepOneProps) => {
    const {showToast} = useToaster()

    const {data: planInfo, isLoading} = bff.paywall.planUpdateModal.getPlansInfo.useQuery(
        undefined,
        {
            onError(error) {
                reportError(error)
                showToast(t`An error occurred. Please try again later or contact support.`, {
                    level: 'error',
                })
            },
        },
    )

    const {title} = getPlanUpdateModalConf({pageOrigin})
    const isMobile = useMediaQuery(`(max-width: ${breakpoints.tabletUp}`)

    const upgradeToPlan = advancedFeatures.includes(pageOrigin) ? 'ADVANCED' : 'ESSENTIAL'
    const upgradeToPlanPrices = planInfo?.plansPrices?.[upgradeToPlan]!
    const currentPlan = planInfo?.currentPlan as AllowedPlanType

    useEffect(() => {
        trackChoosePlanModalActioned({
            action: 'viewed',
            step: 'pricing_plan',
            origin: pageOrigin,
            section: pageSection,
        })
    }, [])

    return (
        <>
            {title && (
                <StyledModalTitle id="plan-update-modal-title">
                    {!isMobile && <HighFive size={32} />}
                    {title}
                </StyledModalTitle>
            )}
            <StyledModalContent>
                {isLoading ? (
                    <Loading />
                ) : (
                    <Inline space={10} justifyContent="center" alignItems="start">
                        {!isMobile && (
                            <PlanColumn
                                planType={currentPlan}
                                currentPlan={currentPlan}
                                planPrices={planInfo?.plansPrices?.[currentPlan]}
                                pageOrigin={pageOrigin}
                                isMobile={isMobile}
                            />
                        )}
                        <PlanColumn
                            planType={upgradeToPlan}
                            currentPlan={currentPlan}
                            planPrices={upgradeToPlanPrices}
                            pageOrigin={pageOrigin}
                            onGetStarted={() =>
                                onGetStarted({
                                    upgradeToPlan,
                                    existingPlan: currentPlan,
                                })
                            }
                            isMobile={isMobile}
                        />
                    </Inline>
                )}
            </StyledModalContent>
            <StyledModalFooter>
                <Link
                    href={`${config.endpoints.commercial}/pricing`}
                    target="_blank"
                    onClick={() =>
                        trackChoosePlanModalActioned({
                            action: 'view_all_link',
                            step: 'pricing_plan',
                            origin: pageOrigin,
                            section: pageSection,
                        })
                    }
                >
                    <Trans>View and compare all plans</Trans>
                </Link>
            </StyledModalFooter>
        </>
    )
}

interface PlanColumnProps {
    planType: AllowedPlanType
    planPrices?: PlanPrices
    pageOrigin: PageOrigin
    currentPlan: AllowedPlanType
    onGetStarted?: () => void
    isMobile?: boolean
}

export const PlanColumn = ({
    planType,
    planPrices,
    pageOrigin,
    currentPlan,
    onGetStarted,
    isMobile,
}: PlanColumnProps) => {
    const currency = planPrices?.currency ?? ''

    const fromPrice = toStringNoDecimal(planPrices?.prices?.monthlyDiscounted, currency)
    const priceExtBookkeeperStarter =
        starterAddonsPricesPerCurrencyMap.EXTERNAL_BOOKKEEPER[currency]
    const priceExtBookkeeperEssentialAndAdvanced = toStringNoDecimal(0, currency)
    const priceAtmWithdrawal = starterAddonsPricesPerCurrencyMap.ATM_WITHDRAWALS[currency]

    const {pillLabel, plansConf} = getPlanUpdatePagesConf({
        pageOrigin,
        priceExtBookkeeperStarter,
        priceExtBookkeeperEssentialAndAdvanced,
        priceAtmWithdrawal,
    })

    const {pricing3CopyExperiment} = useFlags()

    const planColumnType =
        planType === currentPlan || (planType === 'ADVANCED' && currentPlan === 'STARTER')
            ? 'SIMPLE'
            : 'AWESOME'

    const planColumnConf = plansConf?.[planType]?.[planColumnType]

    return (
        <PlanColumnWrapper $isActive={!!onGetStarted}>
            <Inline space={10} alignItems="center" justifyContent="start">
                <Text variant="2xlarge-accent">{PlanTypeName[planType]}</Text>
                {currentPlan === planType ? (
                    <Tag variant="gray">
                        <Trans>Your current plan</Trans>
                    </Tag>
                ) : (
                    <Tag variant="purple">{pillLabel}</Tag>
                )}
            </Inline>
            <Box paddingTop={10}>
                <SmallText>{planColumnConf?.description}</SmallText>
            </Box>
            {fromPrice === toStringNoDecimal(0, planPrices?.currency!) &&
            !pricing3CopyExperiment ? (
                <Box paddingTop={40} marginTop={2}>
                    <Text variant="3xlarge-accent">
                        <Trans>FREE</Trans>
                    </Text>
                </Box>
            ) : (
                <Box paddingTop={28}>
                    <FromText variant="medium-default">
                        <Trans>from</Trans>
                    </FromText>
                    <Inline justifyContent="start" alignItems="baseline" space={4}>
                        <Trans>
                            <Text variant="3xlarge-accent">{fromPrice}</Text>
                            <SmallText>/ month</SmallText>
                        </Trans>
                    </Inline>
                </Box>
            )}
            <Box paddingTop={24}>
                <Text variant="medium-default">{planColumnConf?.featuresLabel}</Text>
            </Box>
            <FeaturesList paddingTop={10}>
                {planColumnConf?.featuresList.map((feature) => (
                    <Inline
                        justifyContent="start"
                        alignItems="center"
                        space={6}
                        key={feature.title?.toString()}
                    >
                        <CheckSmall size={16} color={tokens.colorContentPositive} />
                        {feature.tooltip && !isMobile ? (
                            <Tooltip content={feature.tooltip}>
                                <DashedText>{feature.title}</DashedText>
                            </Tooltip>
                        ) : (
                            <SmallText>{feature.title}</SmallText>
                        )}
                    </Inline>
                ))}
            </FeaturesList>
            <HorizontalLine />
            <PlanColumnLower>
                {planColumnConf?.benefitsList.map((benefit) =>
                    benefit.tooltip && !isMobile ? (
                        <Tooltip key={benefit.title?.toString()} content={benefit.tooltip}>
                            <DashedText>{benefit.title}</DashedText>
                        </Tooltip>
                    ) : (
                        <SmallText key={benefit.title?.toString()}>{benefit.title}</SmallText>
                    ),
                )}
                {onGetStarted && (
                    <StyledButton variant="primary" onClick={onGetStarted}>
                        <Trans>Get started</Trans>
                    </StyledButton>
                )}
            </PlanColumnLower>
        </PlanColumnWrapper>
    )
}

const PlanColumnWrapper = styled.div<{$isActive: boolean}>`
    text-align: left;
    width: auto;
    height: auto;
    max-width: 358px;
    box-sizing: border-box;
    padding: ${tokens.spacing24} ${tokens.spacing32};
    border-radius: ${tokens.spacing8};
    border: ${({$isActive}) =>
        $isActive
            ? `${tokens.sizeBorderDefault} solid ${tokens.colorBorderDiscover}`
            : `${tokens.sizeBorderDefault} solid ${tokens.colorBorderStatic}`};
    background-color: ${({$isActive}) =>
        $isActive ? tokens.colorBackgroundStatic : tokens.colorBackgroundStaticLouder};
    box-shadow: ${({$isActive}) => ($isActive ? tokens.shadowRaise : '')};
    color: ${tokens.colorContentStatic};
`

const FeaturesList = styled(Box)`
    height: 110px;
    box-sizing: border-box;
`

const HorizontalLine = styled.hr`
    border: none;
    background-color: ${tokens.colorBorderStatic};
    height: ${tokens.sizeBorderDefault};
    margin: ${tokens.spacing20} -${tokens.spacing32};
`

const PlanColumnLower = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    height: 150px;
    padding: 0 ${tokens.spacing8};
`

const FromText = styled(Text)`
    margin-bottom: -${tokens.spacing8};
`

const StyledButton = styled(Button)`
    width: 100%;
    margin-top: auto;
`

const StyledModalTitle = styled(ModalTitle)`
    display: flex;
    justify-content: center;
    align-items: center;
    grid-gap: ${tokens.spacing10};
`

const StyledModalFooter = styled(ModalFooter)`
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${tokens.colorBackgroundStaticLouder};
    margin-top: ${tokens.spacing28};
`

const StyledModalContent = styled(ModalContent)`
    min-height: 550px;
    display: flex;
    justify-content: center;
    align-items: center;
`
